<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRightIcon } from 'lucide-vue-next';
import Button from '@/components/ui/button/Button.vue';
import type { Category } from '@/types/category';

interface Props {
  category: Category;
  variant?: 'large' | 'small';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'small',
});

const isLarge = props.variant === 'large';
</script>

<template>
  <div
    class="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:shadow-xl"
    :class="isLarge ? 'min-h-125' : 'min-h-55'"
  >
    <div class="absolute inset-0">
      <img
        :src="category.image"
        :alt="category.name"
        loading="lazy"
        class="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
      />

      <div
        class="absolute inset-0 bg-linear-to-t from-black/80 via-black/40 to-black/20"
        :class="isLarge ? 'via-black/50' : 'via-black/40'"
      />
    </div>

    <div
      class="relative z-10 flex h-full flex-col justify-end p-6 text-white"
      :class="isLarge ? 'items-start' : 'items-start'"
    >
      <span
        v-if="category.badge"
        class="mb-3 inline-flex items-center rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white"
      >
        {{ category.badge }}
      </span>

      <h3 class="mb-2 font-bold text-white" :class="isLarge ? 'text-3xl' : 'text-xl'">
        {{ category.name }}
      </h3>

      <p class="mb-4 text-gray-200" :class="isLarge ? 'max-w-md text-base' : 'text-sm'">
        {{ category.description }}
      </p>

      <template v-if="isLarge">
        <Button as-child variant="secondary" class="border-0 bg-green-500 text-white hover:bg-green-600">
          <Link :href="category.link" target="_blank"> Смотреть категорию </Link>
        </Button>
      </template>
      <template v-else>
        <Link
          :href="category.link"
          target="_blank"
          class="group/link inline-flex items-center gap-1 text-sm font-semibold text-white transition-colors hover:text-green-400"
        >
          Перейти
          <ArrowRightIcon class="size-4 transition-transform group-hover/link:translate-x-1" />
        </Link>
      </template>
    </div>
  </div>
</template>
